export class EquirectRenderer {
  constructor(canvas, video) {
    this.canvas = canvas;
    this.video = video;
    this.perf = { maxFps: 60, useRVFC: true, qualityBias: 1, dprMin: 0.5, dprMax: 3.0, quality: "high" };
    this.lastResize = 0;
    this.lastFov = 80;
    this.uniforms = {};

    const gl = canvas.getContext("webgl2");
    if (!gl) throw new Error("WebGL2 not available");
    this.gl = gl;

    // Vertex shader
    const vs = `#version 300 es
precision highp float;
layout(location=0) in vec2 a_pos;
out vec2 v_uv;
void main(){ v_uv=(a_pos+1.0)*0.5; gl_Position=vec4(a_pos,0.0,1.0);} `;

    // Fragment shader for equirectangular 360 video
    const fs = `#version 300 es
precision highp float;
in vec2 v_uv; out vec4 o; uniform sampler2D u_tex;
uniform vec3 u_dir; // yaw,pitch,fov in radians
vec3 ray(vec2 uv, float fov){ float x=(uv.x-0.5)*2.0; float y=(uv.y-0.5)*2.0; float z=1.0/ tan(fov*0.5); return normalize(vec3(x, -y, z)); }
mat3 rotY(float a){ float c=cos(a), s=sin(a); return mat3(c,0.,-s, 0.,1.,0., s,0.,c);} 
mat3 rotX(float a){ float c=cos(a), s=sin(a); return mat3(1.,0.,0., 0.,c,-s, 0.,s,c);} 
void main(){ vec3 r = ray(v_uv, u_dir.z); r = rotY(u_dir.x) * rotX(u_dir.y) * r; float lon = atan(r.z, r.x); float lat = asin(clamp(r.y,-1.,1.)); vec2 eUV = vec2( (lon/3.1415926 + 1.0)*0.5, (lat/1.5707963 + 0.5) ); eUV.x = fract(eUV.x); o = texture(u_tex, eUV); }`;

    const vsObj = this.mkShader(this.gl.VERTEX_SHADER, vs);
    const fsObj = this.mkShader(this.gl.FRAGMENT_SHADER, fs);
    const prog = this.gl.createProgram();
    this.gl.attachShader(prog, vsObj);
    this.gl.attachShader(prog, fsObj);
    this.gl.linkProgram(prog);
    
    if (!this.gl.getProgramParameter(prog, this.gl.LINK_STATUS)) {
      throw new Error(String(this.gl.getProgramInfoLog(prog)));
    }
    this.program = prog;

    // Create vertex buffer
    const buf = this.gl.createBuffer();
    this.posBuf = buf;
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buf);
    const quad = new Float32Array([-1,-1, 1,-1, -1,1, 1,-1, 1,1, -1,1]);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, quad, this.gl.STATIC_DRAW);

    // Create texture
    const tex = this.gl.createTexture();
    this.tex = tex;
    this.gl.bindTexture(this.gl.TEXTURE_2D, tex);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

    // Get uniform locations
    this.uniforms.u_tex = this.gl.getUniformLocation(prog, "u_tex");
    this.uniforms.u_dir = this.gl.getUniformLocation(prog, "u_dir");

    // Setup resize observer
    const ro = new ResizeObserver(() => this.resize(this.lastFov || 80));
    ro.observe(canvas);
  }

  mkShader(type, src) {
    const s = this.gl.createShader(type);
    this.gl.shaderSource(s, src);
    this.gl.compileShader(s);
    if (!this.gl.getShaderParameter(s, this.gl.COMPILE_STATUS)) {
      throw new Error(String(this.gl.getShaderInfoLog(s)));
    }
    return s;
  }

  setPerf(opts) {
    this.perf = { ...this.perf, ...opts };
    // Apply quality presets
    if (opts.quality) {
      const presets = {
        low: { qualityBias: 0.7, dprMin: 0.3, dprMax: 1.5, maxFps: 30 },
        medium: { qualityBias: 1.0, dprMin: 0.5, dprMax: 2.0, maxFps: 45 },
        high: { qualityBias: 1.2, dprMin: 0.7, dprMax: 2.5, maxFps: 60 },
        ultra: { qualityBias: 1.5, dprMin: 1.0, dprMax: 3.0, maxFps: 60 }
      };
      const preset = presets[opts.quality];
      if (preset) {
        this.perf = { ...this.perf, ...preset };
      }
    }
  }

  resize(fovDeg) {
    const now = performance.now();
    if (now - this.lastResize < 200) return; // Increased debounce time
    this.lastResize = now;
    
    // Prevent resize during render to avoid ResizeObserver loops
    if (this._isRendering) return;
    
    // Get video dimensions for better quality calculation
    const videoWidth = this.video.videoWidth || 1920;
    const videoHeight = this.video.videoHeight || 1080;
    // const videoAspect = videoWidth / videoHeight; // Reserved for future use
    
    // Calculate base DPR with quality bias
    const baseDpr = (window.devicePixelRatio || 1) * this.perf.qualityBias;
    
    // Improved FOV scaling - less aggressive, maintains quality at different zoom levels
    const fovScale = Math.pow(80 / Math.max(30, Math.min(120, fovDeg)), 0.3);
    
    // Consider video resolution for optimal scaling
    const resolutionScale = Math.min(2.0, Math.max(0.5, Math.sqrt((videoWidth * videoHeight) / (1920 * 1080))));
    
    // Calculate target DPR with better bounds
    const targetDpr = Math.max(
      this.perf.dprMin, 
      Math.min(this.perf.dprMax, baseDpr * fovScale * resolutionScale)
    );
    
    const w = Math.round(this.canvas.clientWidth * targetDpr);
    const h = Math.round(this.canvas.clientHeight * targetDpr);
    
    if (Math.abs(this.canvas.width - w) > 2 || Math.abs(this.canvas.height - h) > 2) { 
      this.canvas.width = w; 
      this.canvas.height = h; 
    }
  }

  render(yawRad, pitchRad, fovRad) {
    this._isRendering = true;
    
    // Convert radians to degrees for internal calculations
    const fovDeg = fovRad * 180 / Math.PI;
    // const yawDeg = yawRad * 180 / Math.PI; // Reserved for future use
    // const pitchDeg = pitchRad * 180 / Math.PI; // Reserved for future use
    
    this.lastFov = fovDeg;
    this.resize(fovDeg);
    
    const gl = this.gl;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.useProgram(this.program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    
    if (this.video.readyState >= this.video.HAVE_CURRENT_DATA && this.video.videoWidth > 0) {
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.video);
        // Debug: Log video dimensions for 360 streams
        if (this.video.videoWidth === 3840 && this.video.videoHeight === 1920) {
        }
      } catch (e) {
        console.warn('WebGL texture update failed:', e);
      }
    } else {
      // Debug: Log video state
      console.log('Video not ready:', {
        readyState: this.video.readyState,
        videoWidth: this.video.videoWidth,
        videoHeight: this.video.videoHeight,
        currentTime: this.video.currentTime
      });
    }
    
    gl.uniform1i(this.uniforms.u_tex, 0);
    gl.uniform3f(this.uniforms.u_dir, yawRad, pitchRad, fovRad);
    
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.posBuf);
    this.gl.enableVertexAttribArray(0);
    this.gl.vertexAttribPointer(0, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
    
    this._isRendering = false;
  }
}
